export type PersistedMartinaRunPointer = {
  requestId: string;
  runId: string;
  conversationId: string;
};

export type MartinaTurnSendPlan =
  | { mode: 'reconnect'; requestId: string; runId: string }
  | { mode: 'enqueue'; requestId: string };

/**
 * Interactive retries mint a fresh requestId so a completed failure is not
 * replayed. A still-running accepted job must be resumed instead of enqueued
 * twice when the browser lost the 202 or the delivery poller dropped offline.
 *
 * F-004: if the previous attempt already minted a requestId (user turn may be
 * persisted) but sessionStorage never got a runId pointer, reuse that id on
 * Riprova instead of minting a duplicate user row.
 */
export function planMartinaTurnSend(input: {
  conversationId: string;
  mintRequestId: () => string;
  persisted?: PersistedMartinaRunPointer | null;
  /** Last optimistic requestId for this conversation before pointer write. */
  pendingRequestId?: string | null;
}): MartinaTurnSendPlan {
  const persisted = input.persisted;
  if (
    persisted
    && persisted.conversationId === input.conversationId
    && persisted.requestId
    && persisted.runId
  ) {
    return { mode: 'reconnect', requestId: persisted.requestId, runId: persisted.runId };
  }
  if (input.pendingRequestId) {
    return { mode: 'enqueue', requestId: input.pendingRequestId };
  }
  return { mode: 'enqueue', requestId: input.mintRequestId() };
}

export function isActiveMartinaRunStatus(status: string | null | undefined): boolean {
  return status === 'queued' || status === 'running' || status === 'leased';
}

/**
 * After a transport failure on POST /turns, recover the already-accepted job
 * by requestId instead of minting a duplicate enqueue on Riprova.
 */
export function shouldResumeAcceptedTurnAfterPostFailure(run: {
  status?: string | null;
  runId?: string | null;
} | null | undefined): boolean {
  if (!run?.runId) return false;
  return isActiveMartinaRunStatus(run.status) || ['completed', 'failed', 'cancelled'].includes(String(run.status ?? ''));
}

/**
 * F-003: anti-loss match prefers durable clientRequestId over DB message id
 * (UUID ≠ client request id) and over plainText (ambiguous for repeats).
 */
export function isSavedUserTurnForRequest(input: {
  requestId: string;
  plainText: string;
  message: {
    role?: string | null;
    id?: string | null;
    clientRequestId?: string | null;
    plainText?: string | null;
  };
}): boolean {
  if (input.message.role !== 'user') return false;
  if (input.message.clientRequestId && input.message.clientRequestId === input.requestId) return true;
  if (input.message.id === input.requestId) return true;
  return (input.message.plainText ?? '').trim() === input.plainText.trim();
}
